/**
 * Legacy Map Component - Kept for backward compatibility
 * For new implementations, use AdvancedMapView from components/map/AdvancedMapView
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import GoogleMap from "google-map-react";
import { is } from "src/util/is";

const LIGHT_MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  {
    featureType: "administrative.land_parcel",
    elementType: "labels.text.fill",
    stylers: [{ color: "#bdbdbd" }],
  },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
];

const DARK_MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#1f1f1f" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1f1f1f" }] },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [{ color: "#444444" }],
  },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#2a2a2a" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d6d6d6" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2f2f2f" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#d0d0d0" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#e0e0e0" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f171f" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#a6c0ff" }] },
];

function Map({
  onPlaces,
  onMapClick = null,
  lat,
  lng,
  address,
  searchBar = true,
  data = null,
}) {
  const theme = useTheme();
  const { mode } = theme.palette;
  const mapStyles = useMemo(
    () => (mode === "dark" ? DARK_MAP_STYLE : LIGHT_MAP_STYLE),
    [mode]
  );
  const [map, setMap] = useState(null);
  let markers = useRef([]);
  const geocoderRef = useRef(null);
  const mapClickListenerRef = useRef(null);
  
  const onSearchBar = useCallback((map) => {
    let markers = [];
    const { places: { SearchBox }, LatLngBounds, Size, Point, Marker, LatLng } = window.google.maps;
    const input = document.getElementById("places");

    if (input) {
      const searchBox = new SearchBox(input);
      map.addListener("bounds_changed", () => {
        searchBox.setBounds(map.getBounds());
      });

      map.addListener("center_changed", () => {
        map.setZoom(14);
      });

      searchBox.addListener("places_changed", () => {
        const places = searchBox.getPlaces();
        const { geometry: { location } } = places[0];
        const center = new LatLng(location.lat(), location.lng());
        const bounds = new LatLngBounds();
        if (!places.length) return;
        
        markers.forEach((marker) => marker.setMap(null));
        markers = [];
        places.forEach((place) => {
          if (!place.geometry && !place.geometry.location) return;
          
          const icon = {
            url: place.icon,
            size: new Size(71, 71),
            origin: new Point(0, 0),
            anchor: new Point(17, 34),
            scaledSize: new Size(28, 28),
          };
          markers.push(new Marker({ map, icon, title: place.name, position: place.geometry.location }));
          
          if (place.geometry.viewport) {
            bounds.union(place.geometry.viewport);
          } else {
            bounds.extend(place.geometry.viewport);
          }
        });
        map.fitBounds(bounds);
        map.setCenter(center);
        onPlaces(places);
      });
    }
    setMap(map);
  }, [onPlaces]);

  const onMarker = useCallback((mapInstance) => {
    if (data && mapInstance && window.google) {
      const { LatLng, Marker, InfoWindow } = window.google.maps;
      if (markers.current.length) {
        markers.current.forEach((marker) => marker.setMap(null));
        markers.current = [];
      }
      data.forEach((d) => {
        const latLng = new LatLng(Number(d.locationLatitude), Number(d.locationLongitude));
        const infoWindow = new InfoWindow({
          content: `<div class="address_component">${d.street}</div>`,
          ariaLabel: "address",
        });
        const marker = new Marker({
          position: latLng,
          icon: {
            path: "M12 2C8.13 2 5 5.13 5 9c0 3.87 7 13 7 13s7-9.13 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
            fillColor: "#05c93c",
            fillOpacity: 1,
            strokeColor: "white",
            strokeWeight: 2,
            scale: 2,
            anchor: new window.google.maps.Point(12, 24),
          },
          map: mapInstance,
        });
        markers.current.push(marker);
        marker.addListener("mouseover", () => infoWindow.open({ anchor: marker, map: mapInstance }));
        marker.addListener("mouseout", () => infoWindow.close());
      });
      mapInstance.setOptions({ styles: mapStyles });
      mapInstance.setCenter(new LatLng(25.6663075, 78.4528179));
    }
  }, [data, mapStyles]);

  const initializeMapClick = useCallback(
    (mapInstance) => {
      if (
        !mapInstance ||
        typeof onMapClick !== "function" ||
        !window.google?.maps
      ) {
        return;
      }

      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }

      if (mapClickListenerRef.current) {
        window.google.maps.event.removeListener(mapClickListenerRef.current);
        mapClickListenerRef.current = null;
      }

      mapClickListenerRef.current = mapInstance.addListener("click", (event) => {
        const { latLng } = event || {};
        if (!latLng) return;

        const fallbackResult = () => {
          onMapClick([
            {
              address_components: [],
              formatted_address: `${latLng.lat().toFixed(6)}, ${latLng
                .lng()
                .toFixed(6)}`,
              geometry: { location: latLng },
            },
          ]);
        };

        if (!geocoderRef.current) {
          fallbackResult();
          return;
        }

        geocoderRef.current.geocode({ location: latLng }, (results, status) => {
          if (status === "OK" && Array.isArray(results) && results.length) {
            onMapClick(results);
          } else {
            fallbackResult();
          }
        });
      });
    },
    [onMapClick]
  );

  const handleMapApiLoaded = useCallback(
    ({ map }) => {
      if (window.google) {
        onSearchBar(map);
        map.setOptions({ styles: mapStyles });
        initializeMapClick(map);
      }
    },
    [initializeMapClick, mapStyles, onSearchBar]
  );

  useEffect(() => {
    if (lat && lng && !is.empty(address) && map && !data) {
      const { LatLng, Marker } = window.google.maps;
      const input = document.getElementById("places");
      const position = new LatLng(lat, lng);
      input && (input.value = address);
      map.setCenter(position);
      if (markers?.current?.length) {
        markers.current.forEach((marker) => marker.setMap(null));
        markers.current = [];
      }
      markers.current.push(new Marker({ position, map }));
    }

    if (data) {
      onMarker(map);
    }
  }, [lat, lng, address, map, data, onMarker]);

  useEffect(() => {
    if (map && window.google) {
      map.setOptions({ styles: mapStyles });
    }

    if (map) {
      initializeMapClick(map);
    }

    return () => {
      if (mapClickListenerRef.current && window.google?.maps?.event) {
        window.google.maps.event.removeListener(mapClickListenerRef.current);
        mapClickListenerRef.current = null;
      }
    };
  }, [initializeMapClick, map, mapStyles]);

  return (
    <Stack direction="column" spacing={2}>
      {searchBar && (
        <FormControl fullWidth size="small">
          <TextField
            label="Search Place"
            InputLabelProps={{ shrink: true }}
            size="small"
            id="places"
            placeholder="Address"
          />
        </FormControl>
      )}
      <div className="map-container">
        <GoogleMap
          bootstrapURLKeys={{
            key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
            libraries: ["places"],
          }}
          defaultCenter={{ lat: 10.99835602, lng: 77.01502627 }}
          defaultZoom={10}
          options={{ styles: mapStyles }}
          onGoogleApiLoaded={handleMapApiLoaded}
        />
      </div>
    </Stack>
  );
}

Map.defaultProps = {
  onPlaces: () => {},
  onMapClick: null,
  lat: 0,
  lng: 0,
  address: "",
};

export default Map;
